import { CommonModule } from '@angular/common';
import { Component, computed, inject, Signal } from '@angular/core';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { JournalBlockComponent } from '../journal-block/journal-block.component';
import { SettingsService } from '@app/_services/settings.service';
import { UtilsService } from '@app/_services/utils.service';
import { JournalStore } from '@app/_stores';
import { DeletedEntity } from '@app/_interfaces/feature.interface';
import { MatDialog } from '@angular/material/dialog';
import { CacheService } from '@app/_services/cache.service';
import { DependencyService } from '@app/_services/dependency.service';
import { JournalSetup } from '@app/_interfaces/journal.interface';
import { AlertService } from '@app/_services/alert.service';
import { JournalSetupFormComponent } from '../journal-setup-form/journal-setup-form.component';
import { JournalEntriesComponent } from '../journal-entries/journal-entries.component';
import { ModalService } from '@app/_services/modal.service';

@Component({
  selector: 'app-journal-trash-tab',
  imports: [
    CommonModule,
    SvgComponent,
    JournalBlockComponent
  ],
  templateUrl: './journal-trash-tab.component.html',
  styleUrl: './journal-trash-tab.component.scss'
})

export class JournalTrashTabComponent {

  readonly journalStore = inject(JournalStore);
  // Show ALL metadata in trash screen (description is controlled by descriptionType, not show array)
  show: string[] = ['label', 'time', 'reminder', 'duration', 'attachments', 'repeat', 'mood', 'collaboratorsCount', 'hashtag', 'deletedAt'];
  deletedJournals: Signal<DeletedEntity[]> = computed(() => {
    const deletedEntities: DeletedEntity[] = [];
    this.journalStore.setups().forEach(journal => {
      if (journal.deletedAt && !journal.permaDeletedAt && journal.uid === this.cc.user.uid) {
        deletedEntities.push({
          entityName: 'journal',
          entityId: journal.id,
          deletedAt: this.utils.toDate(journal.deletedAt),
          entries: this.ds.getAllDatesOfEntity(journal, 'journal', this.cc.user.uid),
          entityEntry: this.journalStore.getJournalEntry(journal.id),
          isRepeated: true
        });
      }
    });

    deletedEntities.sort((a, b) => {
      return b.deletedAt.getTime() - a.deletedAt.getTime();
    });
    return deletedEntities;
  });

  idToSetup: Signal<Record<string, JournalSetup>> = this.journalStore.idToSetup;

  constructor(
    public ss: SettingsService,
    public dialog: MatDialog,
    public cc: CacheService,
    public ds: DependencyService,
    private alertService: AlertService,
    private utils: UtilsService,
    private modalService: ModalService
  ) { }

  trackByEntity(index: number, entity: DeletedEntity) {
    return entity.entityId;
  };

  openSetup(entityId: string) {
    const setup = this.idToSetup()[entityId];
    const journalData = {
      mode: 'edit',
      value: setup,
    }

    if (this.cc.isNarrowScreen()) {
      const setupDialog = this.modalService.openMainContent(
        JournalSetupFormComponent,
        journalData,
        { disableClose: true, autoFocus: false }
      );
    } else {
      const setupDialog = this.modalService.openLevel1(
        JournalSetupFormComponent,
        journalData,
        { disableClose: true, autoFocus: false }
      );
    }
  };

  openEntities(entityId: string) {
    this.modalService.openLevel2(
      JournalEntriesComponent,
      { entityId: entityId },
      { disableClose: false, autoFocus: false }
    );
  };

  async remove(entityId: string) {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('removeConfirmation_title'),
      description: this.ss.t('removeConfirmation_content'),
      yesButtonText: this.ss.t('common_buttonRemove'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const journal = this.idToSetup()[entityId];
    journal.permaDeletedAt = new Date();
    this.journalStore.updateJournalSetups([journal]);
  };

  async restore(entityId: string) {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('restoreConfirmation_title'),
      description: this.ss.t('restoreConfirmation_content'),
      yesButtonText: this.ss.t('common_restore'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-35',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const journal = this.idToSetup()[entityId];
    journal.deletedAt = null;
    this.journalStore.updateJournalSetups([journal]);
    const isShow = await this.alertService.toast('journalSetupRestored_content', 'common_show', 'common_dismiss');
    if (isShow) {
      this.openSetup(entityId);
    }
  };
}
